import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';

export interface ExportEntry {
  id: string;
  appId: string;
  title: string;
  status: string;
  createdAt: string;
}

export interface ExportsData {
  exportsUsed: number;
  exportsMax: number;
  resetAt: string;
  resetLabel: string;
  tier: string;
  hourlyUsed: number;
  hourlyMax: number;
  mostUsedApp: { appId: string; count: number } | null;
  lastExport: { id: string; appId: string; title: string; createdAt: string } | null;
  recent: ExportEntry[];
}

const EMPTY: ExportsData = {
  exportsUsed: 0,
  exportsMax: 5,
  resetAt: '',
  resetLabel: 'Resets Monday',
  tier: 'free',
  hourlyUsed: 0,
  hourlyMax: 3,
  mostUsedApp: null,
  lastExport: null,
  recent: [],
};

export function useExports() {
  const { apiFetch, isAuthenticated } = useApi();
  const [data, setData] = useState<ExportsData>(EMPTY);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch<ExportsData>('/api/user/exports');
      setData(res);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [apiFetch, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      setData(EMPTY);
    }
  }, [isAuthenticated, refresh]);

  return { ...data, isLoading, error, refresh };
}
